/**
 * Hero Layout Field Component
 * 
 * Allows editing hero layouts with breakpoint-aware presets.
 *
 * NOTE (Feb 2026):
 * iPhone/iPad rendering is currently forced to a single column in `renderer/components/Hero.js`.
 * To avoid a confusing UX, this editor field is temporarily a **unified, desktop-first** preset
 * picker: users choose one layout preset and we apply it to all breakpoints.
 *
 * Layout values represent items per column (e.g., [2, 3] = 2 items in col 1, 3 in col 2).
 * Presets include columnSequences that specify which item types go where.
 * Selecting a preset triggers smart item reorganization.
 * 
 * BACKEND-DRIVEN PRESETS (Nov 2025):
 * - Presets are now fetched from backend API via MetadataContext
 * - Single source of truth: /api/metadata/hero-layout-presets/
 * - Backend validates preset_id for consistency
 * - Automatic caching and loading states
 * 
 * PREVIOUS UPDATES:
 * - Fixed hero data access: Now checks both `component` and `component_id` fields
 * - Added fallback fetching if hero data is incomplete
 * - Improved error messages to be user-friendly
 * - Icons now show color-coded item types (via HeroLayoutPresetPicker)
 * 
 * @param {string} label - Field label
 * @param {string} help - Help text
 * @param {Object} value - Hero layout object { extra_small, small, medium, large, extra_large }
 * @param {Function} onChange - Callback (newValue) => void
 * @param {boolean} isLoading - External loading state
 */

import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import HeroLayoutPresetPicker from './HeroLayoutPresetPicker';
import HeroLayoutPreviewModal from './HeroLayoutPreviewModal';
import { PRESETS as FALLBACK_PRESETS } from './heroLayoutPresets';
import { EditContext } from '../../context/EditContext';
import { useMetadata } from '../../context/MetadataContext';
import { reorganizeHeroItems } from '../../utils/heroReorganization';
import { typography, textColors } from '../../../design-system/tokens';
import { 
  applyLayoutByMode, 
  getDefaultLayout,
  layoutsEqual
} from '../../utils/heroLayoutUtils';

/**
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.help - Help text
 * @param {Object} props.value - Hero layout object with breakpoint keys
 * @param {Function} props.onChange - Callback when layout changes (newValue) => void
 * @param {boolean} props.isLoading - Whether field is in loading state
 */
export default function HeroLayoutField({ label, help, value, onChange, isLoading = false }) {
  // Get hero data from EditContext
  const { selectedElement, selectElement, refreshElement } = useContext(EditContext);
  
  // Get presets from MetadataContext (backend-driven)
  const metadata = useMetadata();
  const presetsData = metadata.heroLayoutPresets;
  const { refetchMetadataKeys } = metadata;

  // Use backend presets when available; fall back to local definitions so the
  // preset picker is always usable even if the API request failed transiently.
  const PRESETS = (presetsData?.presets?.length > 0)
    ? presetsData.presets
    : FALLBACK_PRESETS;

  // Only show the spinner while the initial metadata batch is still in flight
  const presetsLoading = metadata.loading && !presetsData;

  // --- Auto-retry when backend presets are missing ---
  const MAX_PRESETS_RETRIES = 3;
  const [presetsRetryCount, setPresetsRetryCount] = useState(0);

  useEffect(() => {
    if (presetsData) return;                        // already loaded
    if (metadata.loading) return;                   // initial batch still running
    if (presetsRetryCount >= MAX_PRESETS_RETRIES) return; // exhausted retries
    if (typeof refetchMetadataKeys !== 'function') return;

    const delay = 1500 * Math.pow(2, presetsRetryCount); // 1.5 s, 3 s, 6 s
    const timer = setTimeout(async () => {
      try {
        const results = await refetchMetadataKeys(['heroLayoutPresets'], {
          reason: `hero-layout-field:auto-retry:${presetsRetryCount + 1}`,
        });
        const ok = results?.some((r) => r.ok && r.key === 'heroLayoutPresets');
        if (!ok) setPresetsRetryCount((c) => c + 1);
      } catch {
        setPresetsRetryCount((c) => c + 1);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [presetsData, metadata.loading, presetsRetryCount, refetchMetadataKeys]);

  // Show a soft warning only after all retries are exhausted and we're using fallbacks
  const presetsError =
    !metadata.loading && !presetsData && presetsRetryCount >= MAX_PRESETS_RETRIES
      ? 'Could not load presets from server — showing defaults.'
      : null;

  const containerRef = useRef(null);
  const [columns, setColumns] = useState(1);
  const MIN_CARD_WIDTH = 180; // px - ensure cards are big enough
  const GAP_PX = 12; // tailwind gap-3
  
  // Wrap onChange to add error handling
  const handleOnChange = (newValue) => {
    try {
      if (onChange) {
        onChange(newValue);
      } else {
        console.warn('[HeroLayoutField] onChange is not defined');
      }
    } catch (error) {
      console.error('[HeroLayoutField] onChange error:', error);
      throw error;
    }
  };
  
  // Temporarily forced unified editing (no mobile/desktop split, no per-breakpoint editing)
  const MODE = 'one';
  const ACTIVE_BP = 'medium';

  const [isReorganizing, setIsReorganizing] = useState(false);
  const [reorganizationError, setReorganizationError] = useState(null);
  const [reorganizationSuccess, setReorganizationSuccess] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [pendingPreset, setPendingPreset] = useState(null);
  const [previewOperations, setPreviewOperations] = useState(null);
  const [orphanDecisions, setOrphanDecisions] = useState({}); // Map of item ID -> boolean (true = keep, false = delete)
  const [undoStack, setUndoStack] = useState([]);
  const [selectedPresetId, setSelectedPresetId] = useState(null); // Track which preset is currently selected

  // Desktop-first canonical layout value for display/preset matching.
  // Previously unified mode was mobile-first (extra_small), but mobile rendering is forced to one column.
  const currentLayoutValue = useMemo(() => {
    const v = value || {};
    return (
      v.medium ||
      v.large ||
      v.extra_large ||
      v.extra_small ||
      v.small ||
      getDefaultLayout()
    );
  }, [value]);

  // Detect which preset matches the current value to highlight it
  useEffect(() => {
    if (!value || !PRESETS || PRESETS.length === 0) {
      setSelectedPresetId(null);
      return;
    }

    const matchingPreset = PRESETS.find((preset) => {
      if (preset.id === 'custom') return false;
      if (!preset.value || !currentLayoutValue) return false;
      return layoutsEqual(preset.value, currentLayoutValue);
    });

    setSelectedPresetId(matchingPreset ? matchingPreset.id : null);
  }, [value, PRESETS, currentLayoutValue]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (reorganizationSuccess) {
      const timer = setTimeout(() => {
        setReorganizationSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [reorganizationSuccess]);

  // Compute number of preset cards per row based on available width in the panel
  useEffect(() => {
    if (!containerRef.current) return;

    const calcColumns = (width) => {
      if (!width || width <= 0) return 1;
      const perCardWithGap = MIN_CARD_WIDTH + GAP_PX;
      const raw = Math.floor((width + GAP_PX) / perCardWithGap);
      return Math.max(1, Math.min(5, raw));
    };

    const update = () => {
      const width = containerRef.current?.getBoundingClientRect().width || 0;
      const next = calcColumns(width);
      setColumns((prev) => (prev !== next ? next : prev));
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Progress message mapping for better UX
  const progressMessages = {
    validating: 'Validating layout...',
    analyzing: 'Analyzing current items...',
    planning: 'Planning changes...',
    prepared: 'Ready to apply changes',
    updating_layout: 'Applying layout changes...',
    complete: 'Complete!'
  };

  // Fetch complete hero data if needed
  const ensureHeroData = async (heroId) => {
    try {
      const response = await fetch(`/api/items/hero/${heroId}/`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch hero data: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('[HeroLayoutField] Failed to fetch hero data:', error);
      return null;
    }
  };

  // Request preview of operations before executing
  const handlePresetSelect = async (preset) => {
    console.log('[HERO_DEBUG] handlePresetSelect called with:', preset);
    if (isLoading || isReorganizing || !preset || !preset.value) {
      console.log('[HERO_DEBUG] handlePresetSelect skipped (loading/invalid)');
      return;
    }

    // Store the selected preset ID
    setSelectedPresetId(preset.id);

    // Clear previous errors
    setReorganizationError(null);
    setReorganizationSuccess(false);

    // Check if we have hero data
    if (!selectedElement || selectedElement.type !== 'hero') {
      console.log('[HERO_DEBUG] No valid hero selected, applying update locally');
      // Still apply layout update without reorganization
      const updatedValue = applyLayoutByMode(value, preset.value, MODE, ACTIVE_BP);
      
      try {
        console.log('[HERO_DEBUG] Calling onChange with:', updatedValue);
        onChange?.(updatedValue);
      } catch (error) {
        console.error('[HeroLayoutField] onChange failed:', error);
        setReorganizationError(`Failed to update layout: ${error.message}`);
      }
      return;
    }

    // Validate and ensure we have complete hero data
    let heroData = selectedElement;
    const heroId = selectedElement.id;
    
    if (!heroId) {
      console.log('[HERO_DEBUG] Hero missing ID, applying update locally');
      // Still apply layout update
      const updatedValue = applyLayoutByMode(value, preset.value, MODE, ACTIVE_BP);
      
      try {
        console.log('[HERO_DEBUG] Calling onChange with:', updatedValue);
        onChange?.(updatedValue);
      } catch (error) {
        console.error('[HeroLayoutField] onChange failed:', error);
        setReorganizationError(`Failed to update layout: ${error.message}`);
      }
      return;
    }

    // Check for component ID (could be 'component' or 'component_id')
    const componentId = selectedElement.component_id || selectedElement.component;
    const currentChildren = selectedElement.children || [];

    // If we're missing critical data, fetch it
    if (!componentId || !currentChildren || currentChildren.length === 0) {
      console.log('[HERO_DEBUG] Fetching full hero data for ID:', heroId);
      const fetchedData = await ensureHeroData(heroId);
      if (!fetchedData) {
        // Still apply layout update even if fetch fails
        const updatedValue = applyLayoutByMode(value, preset.value, MODE, ACTIVE_BP);
        
        try {
          console.log('[HERO_DEBUG] Fetch failed, calling onChange with local update:', updatedValue);
          onChange?.(updatedValue);
        } catch (error) {
          console.error('[HeroLayoutField] onChange failed:', error);
          setReorganizationError(`Failed to update layout: ${error.message}`);
        }
        return;
      }
      
      heroData = fetchedData;
      
      // Update selectedElement with complete data
      if (selectElement) {
        selectElement({ ...fetchedData, type: 'hero' });
      }
    }

    const finalComponentId = heroData.component_id || heroData.component;
    const finalChildren = heroData.children || [];

    if (!finalComponentId) {
      console.log('[HERO_DEBUG] Still missing component ID, applying update locally');
      // Still apply layout update
      const updatedValue = applyLayoutByMode(value, preset.value, MODE, ACTIVE_BP);
      
      try {
        console.log('[HERO_DEBUG] Calling onChange with:', updatedValue);
        onChange?.(updatedValue);
      } catch (error) {
        console.error('[HeroLayoutField] onChange failed:', error);
        setReorganizationError(`Failed to update layout: ${error.message}`);
      }
      return;
    }

    // Save state for undo
    const stateSnapshot = {
      value: JSON.parse(JSON.stringify(value)),
      children: JSON.parse(JSON.stringify(finalChildren))
    };
    
    try {
      console.log('[HERO_DEBUG] Starting dry run reorganization');
      // Run dry run to get preview of operations
      const dryRunResult = await reorganizeHeroItems(
        heroId,
        finalComponentId,
        preset.value,
        preset,
        finalChildren,
        MODE,
        ACTIVE_BP,
        value,
        {
          dryRun: true,
          heroVersion: heroData.version || 1
        }
      );

      console.log('[HERO_DEBUG] Dry run result:', dryRunResult);

      if (dryRunResult.success && dryRunResult.operations) {
        const operations = dryRunResult.operations;
        const hasOrphans = operations.orphans && operations.orphans.length > 0;

        // Only show preview modal if there are orphaned items
        if (hasOrphans) {
          console.log('[HERO_DEBUG] Orphans detected, showing modal');
          
          // Initialize orphan decisions - default all to DELETE
          // Rationale: If an item is an orphan, the preset didn't need it.
          // User can toggle to KEEP if they want extra content.
          const initialDecisions = {};
          operations.orphans.forEach(orphan => {
            initialDecisions[orphan.id] = false;
          });
          
          setOrphanDecisions(initialDecisions);
          
          // Show preview modal with orphan choices
          setPendingPreset({ preset, stateSnapshot });
          setPreviewOperations(operations);
          setShowPreviewModal(true);
        } else {
          // No orphans - execute immediately without preview
          console.log('[HERO_DEBUG] No orphans, executing immediately');
          await executeReorganization(preset, stateSnapshot);
        }
      } else {
        // If dry run fails, proceed without preview
        console.log('[HERO_DEBUG] Dry run failed or no operations, executing immediately');
        await executeReorganization(preset, stateSnapshot);
      }
    } catch (error) {
      console.error('[HeroLayoutField] Reorganization error:', error);
      setReorganizationError(error.message || 'Failed to preview changes');
    }
  };

  // Execute reorganization after confirmation
  const executeReorganization = useCallback(async (preset, stateSnapshot, customOrphanDecisions = null, presetOrphans = null) => {
    console.log('[HERO_DEBUG] executeReorganization called');
    if (!selectedElement || selectedElement.type !== 'hero') return;

    const heroId = selectedElement.id;
    const componentId = selectedElement.component_id || selectedElement.component;
    const currentChildren = selectedElement.children || [];
    const heroVersion = selectedElement.version || 1;

    if (!componentId) {
      setReorganizationError('Component ID not found. Unable to reorganize items.');
      return;
    }

    setIsReorganizing(true);
    setProgressMessage('Applying layout...');

    try {
      console.log('[HERO_DEBUG] Calling reorganizeHeroItems API');
      const result = await reorganizeHeroItems(
        heroId,
        componentId,
        preset.value,
        preset,
        currentChildren,
        MODE,
        ACTIVE_BP,
        value,
        {
          onProgress: (step, percent) => {
            const message = progressMessages[step] || `Processing... ${percent}%`;
            setProgressMessage(message);
          },
          heroVersion: heroVersion,
          orphanDecisions: customOrphanDecisions || orphanDecisions,
          preselectedOrphans: Array.isArray(presetOrphans) ? presetOrphans : null
        }
      );

      console.log('[HERO_DEBUG] reorganizeHeroItems API result:', result);

      if (result.success) {
        // Add to undo stack
        setUndoStack(prev => [...prev, stateSnapshot]);

        // Refresh hero data in EditContext - fetch full data with all children
        if (result.data && refreshElement) {
          // First update with minimal data from response
          if (selectElement) {
            selectElement({ ...result.data, type: 'hero' });
          }
          // Then fetch full data including all children details
          await refreshElement(heroId, 'hero');
        }
        
        // Show success message with operations summary if available
        if (result.operations_summary) {
          const { matched, created, deleted } = result.operations_summary;
          setProgressMessage(`Layout applied: ${matched} reordered, ${created} created, ${deleted} deleted`);
        } else {
          setProgressMessage('Layout applied successfully!');
        }
        
        // Notify parent about change if we have new layout data
        if (result.data && result.data.hero_layout) {
          console.log('[HERO_DEBUG] Calling onChange with new layout data:', result.data.hero_layout);
          onChange?.(result.data.hero_layout);
        }
        
        setReorganizationSuccess(true);
      } else {
        // Handle specific error types
        if (result.error === 'Version conflict') {
          setReorganizationError('Another change was made. Please refresh and try again.');
        } else {
          setReorganizationError(result.error || 'Failed to apply layout changes');
        }
        setProgressMessage('');
      }
    } catch (error) {
      // Handle version conflict errors
      if (error.message === 'Version conflict') {
        setReorganizationError('Another change was made. Please refresh and try again.');
      } else {
        setReorganizationError(error.message || 'Failed to apply layout changes');
      }
      setProgressMessage('');
      console.error('[HeroLayoutField] Reorganization error:', error);
    } finally {
      setIsReorganizing(false);
    }
  }, [selectedElement, value, selectElement, refreshElement, progressMessages, orphanDecisions]);

  // Handle preview confirmation
  const handlePreviewConfirm = () => {
    if (pendingPreset) {
      setShowPreviewModal(false);
      // Pass the current orphan decisions to the execution
      executeReorganization(pendingPreset.preset, pendingPreset.stateSnapshot, orphanDecisions, previewOperations?.orphans || null);
      setPendingPreset(null);
      setPreviewOperations(null);
    }
  };

  // Handle preview cancellation
  const handlePreviewCancel = () => {
    setShowPreviewModal(false);
    setPendingPreset(null);
    setPreviewOperations(null);
  };

  // Handle undo
  const handleUndo = useCallback(() => {
    if (undoStack.length === 0 || !selectedElement) return;

    const lastState = undoStack[undoStack.length - 1];
    
    // Restore layout value
    onChange?.(lastState.value);
    
    // Remove from undo stack
    setUndoStack(prev => prev.slice(0, -1));
    
    // Show message
    setReorganizationSuccess(false);
    setReorganizationError(null);
  }, [undoStack, selectedElement, onChange]);

  // Single preset picker for all breakpoints (desktop-first display)
  const renderContent = () => (
    <HeroLayoutPresetPicker
      value={currentLayoutValue}
      selectedPresetId={selectedPresetId}
      onChange={handlePresetSelect}
      disabled={isLoading || isReorganizing}
      columns={columns}
      presets={PRESETS}
    />
  );

  return (
    <>
      {/* Preview Modal */}
      <HeroLayoutPreviewModal
        isOpen={showPreviewModal}
        operations={previewOperations}
        preset={pendingPreset?.preset}
        orphanDecisions={orphanDecisions}
        onOrphanDecisionChange={setOrphanDecisions}
        onConfirm={handlePreviewConfirm}
        onCancel={handlePreviewCancel}
      />

      <div className="hero-layout-field" ref={containerRef}>
        {/* Label */}
        <div className="mb-3">
          <label className={`block ${typography.label} ${textColors.secondary}`}>
            {label || 'Hero Layout'}
          </label>
          {help && (
            <p className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>{help}</p>
          )}
        </div>

      {/* Presets Loading Indicator */}
      {presetsLoading && (
        <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded px-3 py-2 mb-3">
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>Loading presets...</span>
        </div>
      )}

      {/* Presets Error / Fallback Indicator */}
      {presetsError && (
        <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-3 py-2 mb-3">
          <svg className="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="flex-1">{presetsError}</span>
          <button
            type="button"
            onClick={() => setPresetsRetryCount(0)}
            className="ml-auto text-xs underline hover:text-amber-800 dark:hover:text-amber-300 flex-shrink-0"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading/Progress Indicator */}
      {(isLoading || isReorganizing) && (
        <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 rounded px-3 py-2 mb-3">
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span>{progressMessage || 'Processing...'}</span>
        </div>
      )}

      {/* Success Message with Undo */}
      {reorganizationSuccess && !isReorganizing && (
        <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded px-3 py-2 mb-3">
          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span>Layout applied</span>
          {undoStack.length > 0 && (
            <button
              onClick={handleUndo}
              className="ml-auto text-xs underline hover:text-green-800 dark:hover:text-green-300"
              title="Undo last change"
            >
              Undo
            </button>
          )}
        </div>
      )}

      {/* Error Message */}
      {reorganizationError && !isReorganizing && (
        <div className="flex items-start gap-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded px-3 py-2 mb-3">
          <svg className="h-4 w-4 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span>{reorganizationError}</span>
        </div>
      )}

        {/* Content — always rendered because FALLBACK_PRESETS guarantees a non-empty list */}
        <div className={(isLoading || isReorganizing || presetsLoading) ? 'opacity-50 pointer-events-none' : ''}>
          {PRESETS.length > 0 ? renderContent() : (
            <div className={`${typography.bodySmall} ${textColors.secondary} text-center py-4`}>
              No presets available
            </div>
          )}
        </div>
      </div>
    </>
  );
}
